import React from 'react';
import type { AuthMode } from '../types';

interface AuthViewProps {
  mode: AuthMode;
  email: string;
  password: string;
  newPassword: string;
  code: string;
  error: string | null;
  message: string | null;
  onModeChange: (mode: AuthMode) => void;
  onEmailChange: (v: string) => void;
  onPasswordChange: (v: string) => void;
  onNewPasswordChange: (v: string) => void;
  onCodeChange: (v: string) => void;
  onLogin: () => void;
  onSignup: () => void;
  onVerify: () => void;
  onRequestVerification: () => void;
  onRequestPasswordReset: () => void;
  onResetPassword: () => void;
}

export function AuthView({
  mode,
  email,
  password,
  newPassword,
  code,
  error,
  message,
  onModeChange,
  onEmailChange,
  onPasswordChange,
  onNewPasswordChange,
  onCodeChange,
  onLogin,
  onSignup,
  onVerify,
  onRequestVerification,
  onRequestPasswordReset,
  onResetPassword
}: AuthViewProps) {
  const resetAnd = (m: AuthMode) => () => onModeChange(m);

  return (
    <div className="app auth-view">
      <div className="auth-container">
        <div className="auth-brand">
          <div className="brand-icon brand-icon-frame brand-icon-auth">
            <img src="/logo-transparent.png" alt="" />
          </div>
          <h1>EDGAR Insights</h1>
          <p>AI-powered trading signals from SEC filings</p>
        </div>

        <div className="auth-card">
          <div className="auth-tabs">
            <button
              className={mode === 'login' ? 'active' : ''}
              onClick={resetAnd('login')}
            >
              Sign In
            </button>
            <button
              className={mode === 'signup' ? 'active' : ''}
              onClick={resetAnd('signup')}
            >
              Sign Up
            </button>
          </div>

          {error && (
            <div className="auth-alert error">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z"/>
              </svg>
              <span className="auth-alert-text">{error}</span>
            </div>
          )}

          {message && (
            <div className="auth-alert success">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
              </svg>
              <span className="auth-alert-text">{message}</span>
            </div>
          )}

          {(mode === 'login' || mode === 'signup') && (
            <form
              className="auth-form"
              onSubmit={(e) => {
                e.preventDefault();
                mode === 'login' ? onLogin() : onSignup();
              }}
            >
              <div className="form-field">
                <label htmlFor="email">Email address</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => onEmailChange(e.target.value)}
                  placeholder="you@company.com"
                  autoComplete="email"
                  required
                />
              </div>
              <div className="form-field">
                <label htmlFor="password">Password</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => onPasswordChange(e.target.value)}
                  placeholder="••••••••"
                  autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                  required
                />
              </div>
              <button type="submit" className="btn-primary" disabled={!email || !password}>
                {mode === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            </form>
          )}

          {mode === 'verify' && (
            <div className="auth-form">
              <div className="form-field">
                <label htmlFor="email">Email address</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => onEmailChange(e.target.value)}
                  placeholder="you@company.com"
                  autoComplete="email"
                />
              </div>
              <div className="form-field">
                <label htmlFor="code">Verification code</label>
                <input
                  id="code"
                  type="text"
                  value={code}
                  onChange={(e) => onCodeChange(e.target.value)}
                  placeholder="Paste your verification code"
                  autoComplete="one-time-code"
                />
              </div>
              <div className="form-actions">
                <button type="button" className="btn-primary" onClick={onVerify} disabled={!code}>
                  Verify Account
                </button>
                <button type="button" className="btn-secondary" onClick={onRequestVerification} disabled={!email}>
                  Resend Code
                </button>
              </div>
            </div>
          )}

          {mode === 'forgot' && (
            <div className="auth-form">
              <div className="form-field">
                <label htmlFor="email">Email address</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => onEmailChange(e.target.value)}
                  placeholder="you@company.com"
                  autoComplete="email"
                />
              </div>
              <div className="form-field">
                <label htmlFor="reset-code">Reset code</label>
                <input
                  id="reset-code"
                  type="text"
                  value={code}
                  onChange={(e) => onCodeChange(e.target.value)}
                  placeholder="Paste your reset code"
                  autoComplete="one-time-code"
                />
              </div>
              <div className="form-field">
                <label htmlFor="new-password">New password</label>
                <input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => onNewPasswordChange(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="new-password"
                />
              </div>
              <div className="form-actions">
                <button type="button" className="btn-primary" onClick={onResetPassword} disabled={!code || !newPassword}>
                  Reset Password
                </button>
                <button type="button" className="btn-secondary" onClick={onRequestPasswordReset} disabled={!email}>
                  Request Code
                </button>
              </div>
            </div>
          )}

          <div className="auth-footer">
            {mode === 'login' && (
              <>
                <button className="link-btn" onClick={resetAnd('forgot')}>
                  Forgot password?
                </button>
                <button className="link-btn" onClick={resetAnd('verify')}>
                  Verify account
                </button>
              </>
            )}
            {mode !== 'login' && (
              <button className="link-btn" onClick={resetAnd('login')}>
                ← Back to sign in
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
